import { Alert, Skeleton, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, useParams } from 'react-router';

import { useGetProjectById } from '@/api/typesense/projects/useGetProjectById';
import { legacyRoutes } from '@/constants/legacyRoutes';
import { routes } from '@/constants/routes';
import { LegacyPage } from '@/legacy/components/layout/LegacyPage';
import {
  ProjectLayout,
  Planning,
  Billing,
  ProjectIndexRedirect,
  ProtectedProjectPage,
} from '@/pages/projects/project';

// Redirect components for legacy URLs
const LegacyPlanningRedirect = () => {
  const { projectId } = useParams<{ projectId: string }>();
  return <Navigate to={routes.projects.planning(projectId!)} replace />;
};

const LegacyBillingRedirect = () => {
  const { projectId } = useParams<{ projectId: string }>();
  return <Navigate to={routes.projects.billing(projectId!)} replace />;
};

const LegacyTeamRedirect = () => {
  const { projectId } = useParams<{ projectId: string }>();
  return <Navigate to={routes.projects.team(projectId!)} replace />;
};

const LegacyFinancialsRedirect = () => {
  const { projectId } = useParams<{ projectId: string }>();
  // Both CreatePage and Index redirect to same financials page
  return <Navigate to={routes.projects.financials(projectId!)} replace />;
};

const LegacySettingsRedirect = () => {
  const { projectId } = useParams<{ projectId: string }>();
  return <Navigate to={routes.projects.settings(projectId!)} replace />;
};

// Components that render legacy pages with correct src URLs
const LegacyTeamPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  return (
    <LegacyPage
      src={legacyRoutes.projectManagement.team(projectId!)}
      isNestedInNewPage
      data-automation-id="ProjectManagementTeam"
    />
  );
};

const LegacyFinancialsPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { data: project, isLoading } = useGetProjectById(projectId!);
  const { t } = useTranslation('projectManagement');

  // Wait for project data to load before determining the URL
  if (isLoading || !project) {
    return <Skeleton variant="rectangular" height="400px" />;
  }

  const noContracts = project.ContractCount === 0;

  // Determine the correct URL based on contract count
  // If no contracts, show CreatePage; otherwise show Index
  const financialsUrl = noContracts
    ? `ProjectManagement/ProjectSubContract/CreatePage/${projectId}`
    : `ProjectManagement/ProjectSubContract/Index/${projectId}`;

  return (
    <Stack>
      {noContracts && <Alert severity="info">{t('NoContractsNotice')}</Alert>}
      <LegacyPage
        src={financialsUrl}
        isNestedInNewPage
        data-automation-id="ProjectManagementFinancials"
      />
    </Stack>
  );
};

const LegacySettingsPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  return (
    <LegacyPage
      src={legacyRoutes.projectManagement.settings(projectId!)}
      isNestedInNewPage
      data-automation-id="ProjectManagementSettings"
    />
  );
};

export const ProjectRouter = () => {
  return [
    // Main project layout with nested routes
    <Route
      key="project-new"
      path={routes.projects.project(':projectId')}
      element={<ProjectLayout />}
    >
      {/* Smart redirect to first available tab */}
      <Route index element={<ProjectIndexRedirect />} />

      {/* Planning - Protected */}
      <Route
        path="planning"
        element={
          <ProtectedProjectPage requiredTab="planning">
            <Planning />
          </ProtectedProjectPage>
        }
      />

      {/* Team - Protected */}
      <Route
        path="team"
        element={
          <ProtectedProjectPage requiredTab="team">
            <LegacyTeamPage />
          </ProtectedProjectPage>
        }
      />

      {/* Financials - Protected */}
      <Route
        path="financials"
        element={
          <ProtectedProjectPage requiredTab="financials">
            <LegacyFinancialsPage />
          </ProtectedProjectPage>
        }
      />

      {/* Billing - Protected */}
      <Route
        path="billing"
        element={
          <ProtectedProjectPage requiredTab="billing">
            <Billing />
          </ProtectedProjectPage>
        }
      />

      {/* Settings - Protected */}
      <Route
        path="settings"
        element={
          <ProtectedProjectPage requiredTab="settings">
            <LegacySettingsPage />
          </ProtectedProjectPage>
        }
      />
    </Route>,
  ];
};

/**
 * Pure redirects from legacy /ProjectManagement/* URLs to new /projects/:id/* URLs
 * These need to be registered at the root level (not wrapped in DefaultLayout)
 * to have higher priority than the legacy catch-all route
 */
export const ProjectLegacyRedirectsPure = () => {
  return [
    <Route
      key="legacy-planning-redirect"
      path={legacyRoutes.projectManagement.planning(':projectId')}
      element={<LegacyPlanningRedirect />}
    />,

    <Route
      key="legacy-team-redirect"
      path={legacyRoutes.projectManagement.resource.group(':projectId')}
      element={<LegacyTeamRedirect />}
    />,

    <Route
      key="legacy-financials-index-redirect"
      path={legacyRoutes.projectManagement.projectSubContract.index(
        ':projectId',
      )}
      element={<LegacyFinancialsRedirect />}
    />,

    <Route
      key="legacy-financials-create-redirect"
      path={legacyRoutes.projectManagement.projectSubContract.create(
        ':projectId',
      )}
      element={<LegacyFinancialsRedirect />}
    />,

    <Route
      key="legacy-billing-redirect"
      path={legacyRoutes.projectManagement.billing(':projectId')}
      element={<LegacyBillingRedirect />}
    />,

    <Route
      key="legacy-settings-redirect"
      path={legacyRoutes.projectManagement.settings(':projectId')}
      element={<LegacySettingsRedirect />}
    />,
  ];
};

/**
 * Catch-all fallback for legacy ProjectManagement URLs (non-project-specific pages)
 * These are pages like /ProjectManagement/Adjustment/* that don't belong to specific projects
 * They should continue to work as legacy pages without redirection
 * This MUST be wrapped in DefaultLayout/LegacyLayout (placed in the legacy route tree)
 */
export const ProjectLegacyFallback = () => {
  return (
    <Route
      key="legacy-other-project-management"
      path={legacyRoutes.projectManagement.fallback}
      element={
        <LegacyPage
          isNestedInNewPage={false}
          data-automation-id="ProjectManagementOther"
        />
      }
    />
  );
};
